import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import * as applyData from './applydata'
import { SubmitState, EMAIL_VALIDATOR, createSubmitStateEffect } from './common'
import {
  Input,
  Checkbox,
  TextArea,
  Select,
  RadioQuestion,
  SectionLabel,
} from './inputs'

import './apply.scss'

const {
  FULL_NAME,
  validateName,
  DOB,
  validateDob,
  ADDRESS,
  validateAddress,
  CONTACT_DETAILS,
  GENDER,
  ETHNICITY,
  EDUCATION,
  validateContactDetails,
  EMERGENCY_CONTACT,
  validateEmergencyContact,
  EMPLOYER_DETAILS,
  validateEmployerDetails,
  SCHOOL,
  SCHOOL_DISTRICT,
  FREQUENCY,
  AVAILABILITY,
  SKILLS,
  HEAR_ABOUT_US,
  SHARED_VOLUNTEER,
  SHARED_VOLUNTEER_INFO,
  BACKGROUND_QUESTIONS,
  BACKGROUND_QUESTIONS_IF_YES,
  validateBackgroundQuestions,
  REFERENCES,
  REFERENCE_1,
  REFERENCE_2,
  validateReference1,
  validateReference2,
  AUTHORIZATION_AND_CONSENT,
  READ_CODE_OF_CONDUCT,
  APPLICANT_SIGNATURE,
} = applyData

const Apply = () => {
  const { register, errors, reset, handleSubmit } = useForm()
  const [submitButtonText, setSubmitButtonText] = useState('Subscribe')
  const [submitState, setSubmitState] = useState(SubmitState.NOT_SUBMITTED)

  const validate = errors => {
    for (let key in errors) {
      if (errors.hasOwnProperty(key)) {
        return false
      }
    }
    return true
  }

  const onSubmit = data => {
    setSubmitState(SubmitState.SUBMITTING)
    let formData = new FormData()
    Object.entries(data).forEach(([key, value]) => {
      formData.append(key, value)
    })
    formData.append('formDataNameOrder', JSON.stringify(Object.keys(data)))

    // Salesforce logic attempt.
    // fetch(process.env.GATSBY_SALESFORCE_API, {
    //   method: 'POST',
    //   body: formData,
    // })
    //   .then(res => {
    //     console.log('res!')
    //     console.dir(res)
    //   })
    //   .catch(err => {
    //     console.log('Salesforce Error: ')
    //     console.dir(err)
    //   })

    fetch(process.env.GATSBY_APPLICATION_FORM_URL, {
      method: 'POST',
      body: formData,
    })
      .then(res => {
        if (res.status === 200) {
          setSubmitState(SubmitState.SUBMITTED)
        }
      })
      .catch(err => {
        console.log('Apps Script error: ' + err)
      })
  }

  useEffect(
    createSubmitStateEffect({
      submitState,
      setSubmitButtonText,
      reset,
      setSubmitState,
      defaultButtonText: 'Apply',
    }),
    [submitState]
  )

  return (
    <form
      method="POST"
      name="apply-form"
      className="apply-form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <h2 className="bold margin-top-large">Basic Information</h2>
      <SectionLabel label="Full name" validator={() => validateName(errors)} />
      {FULL_NAME.map(([title, id], i) => (
        <Input
          title={title}
          id={id}
          register={() => register({ required: true })}
        />
      ))}
      <div
        className={`bold apply-section-label ${
          validateDob(errors) ? '' : 'error-text'
        }`}
      >
        Date of birth
      </div>
      {DOB.map(([title, id], i) => (
        <Input
          title={title}
          id={id}
          register={() => register({ required: true })}
          type="date"
        />
      ))}
      <Select
        label="Gender"
        id="gender"
        options={GENDER}
        errors={errors}
        register={() => register({ required: true })}
        multiple={false}
      />
      <Select
        label="Ethnicity"
        id="ethnicity"
        options={ETHNICITY}
        errors={errors}
        register={() => register({ required: true })}
        multiple={true}
      />
      <Select
        label="Education"
        id="education"
        options={EDUCATION}
        errors={errors}
        register={() => register({ required: true })}
        multiple={false}
      />
      <div
        className={`bold apply-section-label ${
          validateAddress(errors) ? '' : 'error-text'
        }`}
      >
        Address
      </div>
      {ADDRESS.map(([title, id], i) => (
        <Input
          title={title}
          id={id}
          register={() => register({ required: id !== 'addressLine2' })}
        />
      ))}
      <div
        className={`bold apply-section-label ${
          validateContactDetails(errors) ? '' : 'error-text'
        }`}
      >
        Contact details
      </div>
      {CONTACT_DETAILS.map(([title, id], i) => (
        <Input
          title={title}
          id={id}
          register={() =>
            register(
              id === 'email'
                ? EMAIL_VALIDATOR
                : {
                    required: id === 'phoneNumber',
                  }
            )
          }
        />
      ))}
      <div
        className={`bold apply-section-label ${
          validateEmergencyContact(errors) ? '' : 'error-text'
        }`}
      >
        Emergency contact
      </div>
      {EMERGENCY_CONTACT.map(([title, id], i) => (
        <Input
          title={title}
          id={id}
          register={() => register({ required: true })}
        />
      ))}
      <div
        className={`bold apply-section-label ${
          validateEmployerDetails(errors) ? '' : 'error-text'
        }`}
      >
        Employer details (if applicable)
      </div>
      {EMPLOYER_DETAILS.map(([title, id], i) => (
        <Input
          title={title}
          id={id}
          register={() => register({ required: true })}
        />
      ))}
      <h2 className="bold margin-top-large">Volunteer preferences</h2>
      <RadioQuestion
        errors={errors}
        id="frequency"
        question="Frequency"
        options={FREQUENCY}
        register={() => register({ required: true })}
      />
      {[
        [SCHOOL, 'Schools (select one or more)', 'schools'],
        [
          SCHOOL_DISTRICT,
          'School Districts (select one or more)',
          'schoolDistricts',
        ],
        [AVAILABILITY, 'Availability (select one or more)', 'availability'],
        [SKILLS, 'Additional Skills (optional)', 'additionalSkills'],
        [HEAR_ABOUT_US, 'How did you hear about us?', 'hearAboutUs'],
      ].map(([options, label, id]) => (
        <Select
          label={label}
          id={id}
          options={options}
          errors={errors}
          register={() => register({ required: id !== 'additionalSkills' })}
          multiple={id !== 'hearAboutUs'}
        />
      ))}
      <div className="bold apply-section-label">Volunteer notes (optional)</div>
      <TextArea
        title="volunteerNotes"
        id="volunteerNotes"
        register={() => register({ required: false })}
        classes={`body ${errors['volunteerNotes'] ? 'error-text' : ''}`}
      />
      <h2 className="bold margin-top-large">Shared volunteer information</h2>
      <div className="body margin-bottom-large">{SHARED_VOLUNTEER}</div>
      {SHARED_VOLUNTEER_INFO.map(([title, id], i) => (
        <Input
          title={title}
          id={id}
          register={() => register({ required: false })}
        />
      ))}
      <h2
        className={`bold apply-section-label ${
          validateBackgroundQuestions(errors) ? '' : 'error-text'
        }`}
      >
        Background Questions
      </h2>
      {BACKGROUND_QUESTIONS.map(([question, id]) => (
        <RadioQuestion
          errors={errors}
          id={id}
          question={question}
          options={['Yes', 'No']}
          register={() => register({ required: true })}
        />
      ))}
      <div className="bold apply-section-label">
        {BACKGROUND_QUESTIONS_IF_YES}
      </div>
      <TextArea
        title="backgroundInfoAdditionalInfo"
        id="backgroundInfoAdditionalInfo"
        register={() => register({ required: true })}
        classes={`body ${
          errors['backgroundInfoAdditionalInfo'] ? 'error-text' : ''
        }`}
      />
      <h2 className="bold margin-top-large">References</h2>
      <div className="body margin-top-small margin-bottom-large">
        {REFERENCES}
      </div>
      <div
        className={`bold apply-section-label ${
          validateReference1(errors) ? '' : 'error-text'
        }`}
      >
        Reference 1
      </div>
      {REFERENCE_1.map(([title, id], i) => (
        <Input
          title={title}
          id={id}
          register={() => register({ required: true })}
        />
      ))}
      <div
        className={`bold apply-section-label ${
          validateReference2(errors) ? '' : 'error-text'
        }`}
      >
        Reference 2
      </div>
      {REFERENCE_2.map(([title, id], i) => (
        <Input
          title={title}
          id={id}
          register={() => register({ required: true })}
        />
      ))}
      <h2 className="bold margin-top-large">
        Applicant authorization and agreement
      </h2>
      <div className="body auth-consent justify-text">
        {AUTHORIZATION_AND_CONSENT}
      </div>
      <Checkbox
        title={READ_CODE_OF_CONDUCT}
        id="readCodeOfConduct"
        register={() => register({ required: true })}
        checkboxClasses="margin-right-small"
        labelClasses={`body bold ${
          errors['readCodeOfConduct'] ? 'error-text' : ''
        }`}
      />
      <div
        className={`bold margin-top-large apply-section-label ${
          !errors['applicantSignature'] ? '' : 'error-text'
        }`}
      >
        Applicant Signature
      </div>
      <Input
        title={APPLICANT_SIGNATURE}
        id="applicantSignature"
        register={() => register({ required: true })}
      />
      {!validate(errors) && (
        <div className="bold error-text italic margin-top-large">
          Please fill out all required fields
        </div>
      )}
      <button className="body bold button margin-top-medium" type="submit">
        {submitButtonText}
      </button>
    </form>
  )
}

export default Apply
